interface Section {
    id: string;
    barPercent: number;
}

interface ConfiguratorState {
    selectedPropertyType: string | null;
    selections: {
        [key: string]: Array<string | Range>;
    };
    navigation: {
        previousSection: string | null;
        currentSection: string;
        nextSection: string;
    };
}

interface Range {
    min: number | null;
    max: number | null;
}

const sectionsOrdinal: Section[] = [
    { id: '#propertyTypeSection', barPercent: 17 },
    { id: '#locationSection', barPercent: 34 },
    { id: '#priceSection', barPercent: 51 },
    { id: '#floorSection', barPercent: 68 },
    { id: '#areaSection', barPercent: 85 },
    { id: '#summarySection', barPercent: 100 }
];

const sectionToStateKey = {
    '#locationSection': 'selectedLocations',
    '#priceSection': 'selectedPrices',
    '#floorSection': 'selectedFloors',
    '#areaSection': 'selectedAreas'
};

export function initConfigurator() {

    const configuratorState: ConfiguratorState = {
        selectedPropertyType: null,
        selections: {
            selectedLocations: [],
            selectedPrices: [],
            selectedFloors: [],
            selectedAreas: []
        },
        navigation: {
            previousSection: null,
            currentSection: '#propertyTypeSection',
            nextSection: '#locationSection'
        }
    };

    document.addEventListener('DOMContentLoaded', function () {
        document.querySelectorAll<HTMLElement>('#propertyTypeSection .option-item').forEach(el => {
            el.addEventListener('click', function() {
                configuratorState.selectedPropertyType = el.dataset.value;
                showConfiguratorSection('#configuratorFooter');
                goToNextSection();
            });
        });
        
        const setupCheckboxHandlers = (sectionId: string, dataAttribute: string) => {
            const stateKey = sectionToStateKey[sectionId] as keyof typeof configuratorState.selections;
            
            document.querySelectorAll<HTMLElement>(`${sectionId} .option-item-checkbox`).forEach(el => {
                el.addEventListener('click', (clickEvent) => {

                    const changeValue = (checkbox: HTMLInputElement, value: string, minValue: string, maxValue: string) => {
                        if (!checkbox.checked) {
                            if(dataAttribute === 'value'){
                                configuratorState.selections[stateKey] = configuratorState.selections[stateKey].filter(item => item !== value);
                            }
                            if(dataAttribute === 'range'){
                                configuratorState.selections[stateKey] = configuratorState.selections[stateKey].filter(item => {
                                    if (typeof item === 'string') return item !== value;
                                    return !(item.min === (minValue ? Number(minValue) : null) &&
                                        item.max === (maxValue ? Number(maxValue) : null));
                                });
                            }
                        } else {
                            if(dataAttribute === 'value'){
                                configuratorState.selections[stateKey].push(value);
                            }
                            if(dataAttribute === 'range'){
                                configuratorState.selections[stateKey].push({
                                    min: minValue ? Number(minValue) : null,
                                    max: maxValue ? Number(maxValue) : null
                                });
                            }
                        }
                    }
                    
                    const checkbox = el.querySelector('input') as HTMLInputElement;
                    const value = el.dataset.value;
                    const minValue = el.dataset.minValue;
                    const maxValue = el.dataset.maxValue;
                    const clickedElement = clickEvent.target as HTMLElement;
                    
                    if (clickedElement.tagName.toLowerCase() === 'input') {
                        changeValue(clickedElement as HTMLInputElement, value, minValue, maxValue);
                    } else {
                        checkbox.checked = !checkbox.checked;
                        changeValue(checkbox, value, minValue, maxValue);
                    }

                    toggleNextButton(configuratorState.navigation.currentSection);
                });
            });
        };
        
        setupCheckboxHandlers('#locationSection', 'value');
        setupCheckboxHandlers('#priceSection', 'range');
        setupCheckboxHandlers('#floorSection', 'value');
        setupCheckboxHandlers('#areaSection', 'range');
    });
    
    document.querySelectorAll('.select-all').forEach(el => {
        el.addEventListener('click', selectAllCheckboxesInSection);
    });

    document.querySelector('#nextButton')?.addEventListener('click', goToNextSection);
    document.querySelector('#skipButton')?.addEventListener('click', goToNextSection);
    document.querySelector('#backButton')?.addEventListener('click', goToPreviousSection);
    
    document.querySelector('#searchPropertiesButton').addEventListener('click', () => {
        const params = new URLSearchParams();
        
        if (configuratorState.selectedPropertyType) {
            params.append('propertyType', configuratorState.selectedPropertyType);
        }
        
        if (configuratorState.selections.selectedLocations.length > 0) {
            configuratorState.selections.selectedLocations.forEach(location => {
                params.append('locations', location.toString());
            });
        }

        if ( configuratorState.selections.selectedPrices.length > 0 ) {
            configuratorState.selections.selectedPrices.forEach((range: Range | string, index: number) => {
                if ( typeof range !== 'string' ) {
                    if ( range.min !== null ) params.append(`priceMin-${ index }`, range.min.toString());
                    if ( range.max !== null ) params.append(`priceMax-${ index }`, range.max.toString());
                }
            });
        }
        
        if ( configuratorState.selections.selectedFloors.length > 0 ) {
            configuratorState.selections.selectedFloors.forEach(floor => {
                params.append('floors', floor.toString());
            });
        }

        if (configuratorState.selections.selectedAreas.length > 0) {
            configuratorState.selections.selectedAreas.forEach((range: Range | string, index: number) => {
                if (typeof range !== 'string') {
                    if (range.min !== null) params.append(`areaMin-${ index }`, range.min.toString());
                    if (range.max !== null) params.append(`areaMax-${ index }`, range.max.toString());
                }
            });
        }
        
        window.location.href = `/KonfiguratorWyniki?${params.toString()}`;
    });
    
    function selectAllCheckboxesInSection() {
        const { currentSection } = configuratorState.navigation;
        const stateKey = sectionToStateKey[currentSection] as keyof typeof configuratorState.selections;

        if (!stateKey) return;

        const allCurrentCheckboxes = document.querySelectorAll<HTMLElement>(`${currentSection} .option-item-checkbox`);

        allCurrentCheckboxes.forEach(el => {
            const checkbox = el.querySelector('input') as HTMLInputElement;
            if (checkbox.parentElement.classList.contains('d-none')) return;

            if (!checkbox.checked) {
                checkbox.checked = true;
                
                const dataAttribute = ['#locationSection', '#floorSection'].includes(currentSection) ? 'value' : 'range';
                const value = el.dataset.value;
                const minValue = el.dataset.minValue;
                const maxValue = el.dataset.maxValue;
                
                if(dataAttribute === 'value'){
                    if (value && !configuratorState.selections[stateKey].includes(value)) {
                        configuratorState.selections[stateKey].push(value);
                    }
                }
                if(dataAttribute === 'range'){
                    const newRange = { 
                        min: minValue ? Number(minValue) : null, 
                        max: maxValue ? Number(maxValue) : null 
                    };

                    const exists = configuratorState.selections[stateKey].some(item => {
                        if (typeof item === 'string') return false;
                        return item.min === newRange.min && item.max === newRange.max;
                    });

                    if (!exists) {
                        configuratorState.selections[stateKey].push(newRange);
                    }
                }
            }
        });

        toggleNextButton(currentSection);
    }
    
    function goToPreviousSection() {
        const { currentSection, previousSection } = configuratorState.navigation;

        if (!previousSection) return;
        
        hideConfiguratorSection(currentSection);
        showConfiguratorSection(previousSection);
        configuratorState.navigation.nextSection = currentSection;
        configuratorState.navigation.currentSection = previousSection;
        
        const newPreviousSectionIndex = sectionsOrdinal.findIndex(x => x.id === previousSection) - 1;
        configuratorState.navigation.previousSection = newPreviousSectionIndex >= 0 
            ? sectionsOrdinal[newPreviousSectionIndex].id 
            : null;

        if (goToRules(goToPreviousSection))
            return;
        
        const newCurrentSectionIndex = sectionsOrdinal.findIndex(x => x.id === configuratorState.navigation.previousSection);
        if (newCurrentSectionIndex >= 0)
            updateProgressBar(sectionsOrdinal[newCurrentSectionIndex].barPercent);
        
        if (configuratorState.navigation.currentSection === '#propertyTypeSection')
            hideConfiguratorSection('#configuratorFooter');

        toggleNextButton(configuratorState.navigation.currentSection);
    }
    
    function goToNextSection() {
        const { currentSection, nextSection } = configuratorState.navigation;
        
        hideConfiguratorSection(currentSection);
        showConfiguratorSection(nextSection);
        
        configuratorState.navigation.previousSection = currentSection;
        configuratorState.navigation.currentSection = nextSection;
        
        const newNextSectionIndex = sectionsOrdinal.findIndex(x => x.id === nextSection) + 1;
        configuratorState.navigation.nextSection = newNextSectionIndex < sectionsOrdinal.length 
            ? sectionsOrdinal[newNextSectionIndex].id 
            : sectionsOrdinal[sectionsOrdinal.length - 1].id;

        if (goToRules(goToNextSection))
            return;
        
        const previousSectionIndex = sectionsOrdinal.findIndex(x => x.id === currentSection);
        if (previousSectionIndex >= 0) {
            updateProgressBar(sectionsOrdinal[previousSectionIndex].barPercent);
        }
        
        if (configuratorState.navigation.currentSection === '#summarySection') {
            hideConfiguratorSection('#configuratorFooter');
            generateSummary();
        }

        toggleNextButton(configuratorState.navigation.currentSection);
    }
    
    function goToRules(callback: () => void): boolean {
        switch (configuratorState.navigation.currentSection) {
            case '#floorSection':
                if (configuratorState.selectedPropertyType === 'House' || configuratorState.selectedPropertyType === 'Premises') {
                    callback();
                    return true;
                }
                break;
            case '#areaSection':
                if (configuratorState.selectedPropertyType === 'Garage') {
                    callback();
                    return true;
                }
                break;
        }
        return false;
    }
    
    function toggleNextButton(section: string) {
        const nextButton = document.querySelector('#nextButton') as HTMLButtonElement;
        if (!nextButton) return;

        const stateKey = sectionToStateKey[section] as keyof typeof configuratorState.selections;

        let disableButton = false;
        if (stateKey) {
            disableButton = configuratorState.selections[stateKey].length === 0;
        }
        
        if (disableButton) {
            nextButton.classList.add('disabled');
        } else {
            nextButton.classList.remove('disabled');
        }
    }
    
    function updateProgressBar(value: number) {
        const progress = document.querySelector('.progress');
        const progressBar = document.querySelector('.progress-bar');

        if (progress && progressBar) {
            progress.setAttribute('aria-valuenow', value.toString());
            progressBar.setAttribute('style', `width: ${value}%`);
        }
    }

    function showConfiguratorSection(selector: string) {
        const element = document.querySelector(selector);
        if (element) {
            switch (selector) {
                case '#floorSection':
                    const floors = element.querySelectorAll<HTMLElement>('.option-item-checkbox');
                    floors.forEach(checkbox => {
                        if (configuratorState.selections.selectedLocations.some(location => checkbox.dataset.investments.includes(location as string)))
                            checkbox.classList.remove('d-none');
                        else
                            checkbox.classList.add('d-none');
                    });
                    if (configuratorState.selectedPropertyType === 'Garage') {
                        floors.forEach(checkbox => {
                            if (Number(checkbox.dataset.value) > 0)
                                checkbox.classList.add('d-none');
                        })
                    }
                    break;
                case '#locationSection':
                    element.querySelectorAll<HTMLElement>('.option-item-checkbox').forEach(checkbox => {
                        if (checkbox.dataset['propertyTypes'].includes(configuratorState.selectedPropertyType))
                            checkbox.classList.remove('d-none');
                        else
                            checkbox.classList.add('d-none');
                    });
                    break;
                case '#priceSection':
                    if (configuratorState.selectedPropertyType === 'Garage') {
                        const priceRanges = element.querySelectorAll('.option-item-checkbox');
                        priceRanges.forEach(checkbox => {
                            checkbox.classList.add('d-none');
                        })
                        priceRanges.item(0).classList.remove('d-none');   
                    }
                    break;
            }
            element.classList.remove('hide-section');
            element.classList.add('show-section');
        }
    }
    
    function hideConfiguratorSection(selector: string) {
        const element = document.querySelector(selector);
        if (element) {
            element.classList.remove('show-section');
            element.classList.add('hide-section');
        }
    }
    
    function convertToRoman(num: number): string {
        if (num <= 0) return '';

        const romanNumerals = [
            { value: 40, symbol: 'XL' },
            { value: 10, symbol: 'X' },
            { value: 9, symbol: 'IX' },
            { value: 5, symbol: 'V' },
            { value: 4, symbol: 'IV' },
            { value: 1, symbol: 'I' }
        ];

        let result = '';

        for (const { value, symbol } of romanNumerals) {
            while (num >= value) {
                result += symbol;
                num -= value;
            }
        }

        return result;
    }

    function generateSummary() {
        interface SummaryConfig {
            elementId: string;
            sectionId: string;
            stateKey: keyof ConfiguratorState['selections'];
            defaultText: string;
            additionalText: string;
            numberFormat?: Intl.NumberFormat;
            additionalItemTextBack: string;
        }
        
        const initEditButton = (element: Element, isEmpty: boolean, sectionToEdit: string) => {
            const editButton = element.querySelector('svg');
            if (!editButton) return;
            
            if (isEmpty) {
                editButton.classList.add('disabled');
                return;
            }
            
            editButton.addEventListener('click', () => {
                hideConfiguratorSection('#summarySection');
                showConfiguratorSection(sectionToEdit);
                showConfiguratorSection('#configuratorFooter');
                
                configuratorState.navigation.currentSection = sectionToEdit;
                
                const newPrevSectionIndex = sectionsOrdinal.findIndex(x => x.id === sectionToEdit) - 1;
                if (newPrevSectionIndex >= 0) {
                    configuratorState.navigation.previousSection = sectionsOrdinal[newPrevSectionIndex].id;
                    updateProgressBar(sectionsOrdinal[newPrevSectionIndex].barPercent);
                }
                
                const newNextSectionIndex = sectionsOrdinal.findIndex(x => x.id === sectionToEdit) + 1;
                if (newNextSectionIndex < sectionsOrdinal.length) {
                    configuratorState.navigation.nextSection = sectionsOrdinal[newNextSectionIndex].id;
                }

                toggleNextButton(configuratorState.navigation.currentSection);
            });
        };
        
        const summaryConfigs: SummaryConfig[] = [
            { 
                elementId: '#locationSummary', 
                sectionId: '#locationSection', 
                stateKey: 'selectedLocations', 
                defaultText: 'lokalizacja (nie wybrano)',
                additionalText: '',
                additionalItemTextBack: ''
            },
            { 
                elementId: '#priceSummary', 
                sectionId: '#priceSection', 
                stateKey: 'selectedPrices', 
                defaultText: 'cena (nie wybrano)',
                additionalText: 'cena ',
                numberFormat: new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }),
                additionalItemTextBack: ''
            },
            { 
                elementId: '#floorSummary', 
                sectionId: '#floorSection', 
                stateKey: 'selectedFloors', 
                defaultText: 'piętro (nie wybrano)',
                additionalText: '',
                additionalItemTextBack: ''
            },
            { 
                elementId: '#areaSummary', 
                sectionId: '#areaSection', 
                stateKey: 'selectedAreas', 
                defaultText: 'metraż (nie wybrano)',
                additionalText: 'metraż ',
                additionalItemTextBack: ' m²'
            }
        ];
        
        summaryConfigs.forEach(config => {
            const summaryElement = document.querySelector(`#summarySection ${config.elementId}`);
            if (!summaryElement) return;
            switch (config.elementId) {
                case '#floorSummary':
                    if (['House', 'Premises'].includes(configuratorState.selectedPropertyType)) {
                        summaryElement.classList.add('d-none');
                        return;   
                    }
                    break;
                case '#areaSummary':
                    if (configuratorState.selectedPropertyType === 'Garage') {
                        summaryElement.classList.add('d-none');
                        return;   
                    }
                    break;
            }

            const label = summaryElement.querySelector('label');
            if (label) {
                const selections = configuratorState.selections[config.stateKey];
                if (config.stateKey === 'selectedLocations') {
                    label.querySelectorAll('span').forEach(span => {
                       if (selections.includes(span.dataset.value)) {
                           span.classList.remove('d-none');
                       }
                    });
                }
                else {
                    label.innerText = selections.length > 0
                        ? config.additionalText + selections.map(item => {
                        if (typeof item === 'string'){
                            if (config.stateKey === 'selectedFloors') {
                                const level = Number(item);
                                return level === 0 ? 'Parter' : level < 0 ? 'Poziom ' + level : convertToRoman(level) + ' Piętro';
                            }
                            return item;
                        }

                        if (item.min !== null && item.max !== null) {
                            let min = config.numberFormat ? config.numberFormat.format(item.min) : item.min;
                            let max = config.numberFormat ? config.numberFormat.format(item.max) : item.max;
                            return `${min} - ${max}` + config.additionalItemTextBack;
                        } else if (item.min !== null) {
                            let min = config.numberFormat ? config.numberFormat.format(item.min) : item.min;
                            return `${min}+` + config.additionalItemTextBack;
                        } else if (item.max !== null) {
                            let max = config.numberFormat ? config.numberFormat.format(item.max) : item.max;
                            return `do ${max}` + config.additionalItemTextBack;
                        }
                        return '';
                    }).join(', ')
                        : config.defaultText;   
                }
            }

            initEditButton(
                summaryElement, 
                configuratorState.selections[config.stateKey].length !== 0, 
                config.sectionId
            );
        });
    }
}